import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Shield, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSeoMeta } from '@/hooks/useSeoMeta';

export default function CategoryPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/catalog/categories/${categoryId}`),
      api.get('/catalog/services'),
    ])
      .then(([catRes, svcRes]) => {
        setCategory(catRes.data);
        setServices(svcRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [categoryId]);

  useSeoMeta(category ? { seo_title: `${category.name} Authentication & Appraisal - The Fashion Detective`, seo_description: `Expert authentication and appraisal for ${category.name}. Submit photos and get results.` } : null);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground text-sm">Loading...</div>
    </div>
  );

  if (!category) return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 text-center">
      <h1 className="font-display text-2xl font-medium text-ink mb-2">Category Not Found</h1>
      <Link to="/"><Button variant="outline" size="sm"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Home</Button></Link>
    </div>
  );

  return (
    <div data-testid={`category-page-${categoryId}`}>
      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> All Categories
          </Link>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">Category</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.02em] text-ink mb-3" data-testid="category-page-name">
              {category.name}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl mb-6">
              Expert authentication and appraisal for {category.name.toLowerCase()} from the world's top luxury brands.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services for this category */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-xl sm:text-2xl font-medium text-ink mb-6">Choose Your Service</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {services.map(svc => (
              <Card key={svc.id} className="bg-white border-stone/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: svc.family === 'authentication' ? 'rgba(14,107,93,0.10)' : 'rgba(163,106,58,0.10)' }}>
                      {svc.family === 'authentication' ? <Shield className="h-5 w-5" style={{ color: '#0E6B5D' }} /> : <FileText className="h-5 w-5" style={{ color: '#A36A3A' }} />}
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-medium text-ink">{svc.name}</h3>
                      <Badge variant="outline" className="text-[10px]">${(svc.base_price_cents / 100).toFixed(0)} per item</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{svc.description}</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {svc.sla_business_days ? `${svc.sla_business_days} business day${svc.sla_business_days > 1 ? 's' : ''} turnaround` : ''}
                    {svc.family === 'authentication' && ' | Expedited available (+$20)'}
                  </p>
                  <Link to={`/order/submit?category=${categoryId}&service=${svc.family}`}>
                    <Button className="gap-2 rounded-[12px] active:scale-[0.98] w-full sm:w-auto" data-testid={`category-${svc.family}-cta`}>
                      Start {svc.name} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
